import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Upload, Modal, Empty, Icon } from 'antd';
import Title from 'antd/lib/typography/Title';
import { getSessionCookie } from '../../../../utility/cookie-utility';
import { asViaUrl, createPathWithParams, SESSION_ID_PARAM_KEY } from '../../../../utility/via-fetch';
import { inspectionShape } from '../utility/inspection-util';

function getQueryParams(info) {
  return info.inspectionDamageId ? { 
    [SESSION_ID_PARAM_KEY]: getSessionCookie(),
    'damage-id': info.inspectionDamageId,
  } : {
    [SESSION_ID_PARAM_KEY]: getSessionCookie(),
  };
}

function isImageMediaType(type) {
  return type && type.split('/')[0] === 'image';
}

function isVideoMediaType(type) {
  return type && type.split('/')[0] === 'video';
}

/**
 * A video item to display in the gallery.
 * @param {string} props.src The source url of the file.
 * @param {type} props.type The media type of the file.
 */
const PreviewVideo = ({ src, type }) => {
  const video = document.getElementById('videoPlayer');
  if (video && video.load)
  {
    video.load();
  }
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video id="videoPlayer" controls style={{ maxWidth: '100%', maxHeight: '100%' }}>
        <source src={src} type={type} />
      </video>
    </>
  );
}

PreviewVideo.propTypes = {
  src: PropTypes.string,
  type: PropTypes.string,
};

PreviewVideo.defaultProps = {
  src: undefined,
  type: undefined,
};


/**
 * An item to display in the gallery.
 * @param {string} props.src The source url of the file.
 * @param {string} props.filename The name of the file.
 */
const PreviewItem = ({ src, filename }) => (
  <a href={src} style={{color: 'white'}}>{filename}</a>
);

PreviewItem.propTypes = {
  src: PropTypes.string,
  filename: PropTypes.string,
};

PreviewItem.defaultProps = {
  src: undefined,
  filename: undefined,
};


/**
 * A modal for displaying a single gallery item.
 * @param {boolean} props.visible True if the gallery modal is currently visible.
 * @param {object} props.file The file being displayed in the modal.
 */
export function GalleryModal({ switchFile, visible, file, fileList, ...rest }) {
  // const PreviewImage = () => (
  //   <a style={{ display: 'flex', flex: '1 1 auto', maxHeight: '100%', justifyContent: 'center', alignItems: 'center' }} target="_blank" rel="noopener noreferrer" href={file ? file.url : undefined}>
      
  //   </a>
  // );

  const isImage = file && isImageMediaType(file.contentType);
  const isVideo = file && !isImage && isVideoMediaType(file.contentType);
  const isItem = file && !isImage && !isVideo;
  return (
    <Modal width="98%" style={{ width: '90vw', height:'90vh', top:'5vh' }} closeIcon={<Icon type="close" style={{color:"white"}}/>} bodyStyle={{ backgroundColor:"#333", color:'white', height:'90vh' }} visible={visible} footer={null} {...rest}>
      <div className="preview-image-container" style={{ height:'100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection:'column' }}>
        {file && !isItem && file.index > 0 ? <Button onClick={() => switchFile(file.index - 1)} icon="left" style={{ color:"white", backgroundColor:"transparent", border:"none", paddingLeft:"4px", height:"20px", position: 'absolute', top: 'calc(50% - 10px)', left: '0px'}} />  : <></>}
        {file && !isItem && file.name}
        {isImage && <img style={{ maxWidth: '100%', maxHeight:'80vh' }} alt="example" src={file ? file.url : undefined} />}
        {isVideo && <PreviewVideo src={file && file.url} />}
        {isItem && <PreviewItem src={file && file.url} filename={file && file.name} />}
        {file && !isItem && file.index < fileList.length - 1 && (isImageMediaType(fileList[file.index+1].contentType) || isVideoMediaType(fileList[file.index+1].contentType)) ? <Button onClick={() => switchFile(file.index + 1)} icon="right" style={{ color:"white", backgroundColor:"transparent", border:"none", paddingLeft:"4px", height:"20px", position: 'absolute', top: 'calc(50% - 10px)', right: '0px'}} />  : <></>}
      </div>
    </Modal>
  );
}


GalleryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  file: PropTypes.shape({
    url: PropTypes.string.isRequired,
    contentType: PropTypes.string,
    name: PropTypes.string,
  }),
};

GalleryModal.defaultProps = {
  file: null,
};


/**
 * Display the a gallery of the uploaded files in the inspection.
 * @param {object} props.inspection The inspection being displayed.
 */
export default function InspectionGallery({ inspection }) {
  const [fileList, setFileList] = useState([]);
  useEffect(() => {
    if (!inspection) return;

    const isExternal = inspection.externalKey !== null;
    const loggedInPath = asViaUrl(`/api/inspections/${inspection.inspectionCode}/files`);
    const externalPath = asViaUrl(`/api/inspections/external/${inspection.externalKey}/files`);
    const createUrl = info => createPathWithParams(`${isExternal ? externalPath : loggedInPath}/${info.fileId}`, getQueryParams(info));
    const hasThumbnail = contentType => contentType && contentType.startsWith('image/');
    var files = inspection.fileInfo.filter(info => info.contentType && (info.contentType.startsWith('image/') || info.contentType.startsWith('video/'))).map(info => ({
      uid: info.fileId,
      name: info.filename,
      status: 'done',
      thumbUrl: hasThumbnail(info.contentType) ? undefined : '.meaningless-extension',
      url: createUrl(info),
      contentType: info.contentType,
    }));
    for (let index = 0; index < inspection.existingDamages.length; index++)
    {
      const existingDamage = inspection.existingDamages[index];
      for (let fileIndex = 0; fileIndex < existingDamage.fileInfo.length; fileIndex++)
      {
        const info = existingDamage.fileInfo[fileIndex];
        const file = {
          uid: info.fileId,
          name: info.filename,
          status: 'done',
          thumbUrl: hasThumbnail(info.contentType) ? undefined : '.meaningless-extension',
          url: createUrl(info),
          contentType: info.contentType,
        };
        files.push(file);        
      }
    }
    const reports = inspection.fileInfo.filter(info => !info.contentType || (!info.contentType.startsWith('image/') && !info.contentType.startsWith('video/'))).map(info => ({
      uid: info.fileId,
      name: info.filename,
      status: 'done',
      thumbUrl: hasThumbnail(info.contentType) ? undefined : '.meaningless-extension',
      url: createUrl(info),
      contentType: info.contentType,
    }));

    files.push(...reports);
    setFileList(files.map((file, index) => { return {
      ...file,
      index,
    }}));
  }, [inspection]);
  // fileList.find(f => f.index === file.index);
  const [preview, setPreview] = useState({ visible: false, file: undefined });
  const switchFile = newIndex => setPreview({...preview, file: fileList[newIndex]});

  return (
    <div>
      <Title level={3}><FormattedMessage id="gallery" /></Title>
      { fileList.length <= 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        : (
          <Upload
            className="wowee"
            showUploadList={{ showRemoveIcon: false }}
            onPreview={(file) => { setPreview({ visible: true, file: file }); }}
            disabled
            onRemove={false}
            listType="picture-card"
            fileList={fileList}
          />
        )}
      <GalleryModal switchFile={switchFile} visible={preview.visible} file={preview.file} fileList={fileList} onCancel={() => { setPreview({ visible: false, src: undefined }); }} />
    </div>
  );
}

InspectionGallery.propTypes = {
  inspection: inspectionShape.isRequired,
};
